import {useMemo} from 'react';

import {useSingleRunModeContext} from '../../../components/MultiRunWorkspace/SingleRunMode';
import {
  useProjectInternalIdFromNameQuery,
  useProjectNameFromInternalIdQuery,
} from '../../../generated/graphql';
import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {RunSetPart} from '../../../state/views/runSet/types';
import * as Filter from '../../../util/filters';
import {useStatsigGateWorkspaceConsolidation} from '../../../util/rampFeatureFlags';
import {EMPTY_ALL_SELECTIONS} from '../../../util/selectionmanager';
import {useDeferredMemo} from '../../hooks';
import * as FilterTypes from '../filter/types';
import * as GroupSelectionsTypes from '../groupSelections/types';
import {usePart, useParts} from '../hooks';
import * as SortTypes from '../sort/types';
import {WholeFromTypeWithRef} from '../types';
import * as Types from './types';

function assembleRunSet(
  part: Types.RunSetPart,
  groupSelections: GroupSelectionsTypes.GroupSelections,
  sort: SortTypes.Sort,
  filters: FilterTypes.Filter,
  /** if provided, run set should be modified to get data for this run only */
  singleRunName?: string
): Types.RunSetConfig {
  const result = {
    ...part,
    grouping: groupSelections.grouping,
    selections: groupSelections.selections,
    expandedRowAddresses: groupSelections.expandedRowAddresses,
    sort,
    filters,
  };
  if (singleRunName) {
    // for run set to target this specific run, we must override all search,
    // grouping, filters, and selections (aka visibility or "eyeballs")
    result.search.query = '';
    result.grouping = [];
    result.selections = EMPTY_ALL_SELECTIONS;
    result.filters = Filter.rootFilterSingle({
      key: {section: 'run', name: 'name'},
      op: '=',
      value: singleRunName,
    });
  }
  return result;
}

// temporary helper while feature flag is in place.
// once flag is removed, just call useSingleRunModeContext()
const useSingleRunName = () => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const {run} = useSingleRunModeContext();
  return isWorkspaceConsolidationEnabled ? run?.name : undefined;
};

export function useRunSet(ref: Types.Ref): WholeFromTypeWithRef<'runSet'> {
  const part = usePart(ref);
  const groupSelections = usePart(part.groupSelectionsRef);
  const sort = usePart(part.sortRef);
  const filters = usePart(part.filtersRef);
  const singleRunName = useSingleRunName();

  return useMemo(
    () => ({
      ...assembleRunSet(part, groupSelections, sort, filters, singleRunName),
      ref,
    }),
    [ref, part, groupSelections, sort, filters, singleRunName]
  );
}

export function useRunSets(
  refs: Types.Ref[]
): Array<WholeFromTypeWithRef<'runSet'>> {
  const parts = useParts(refs);

  const groupSelectionsRefs = useMemo(
    () => parts.map(part => part.groupSelectionsRef),
    [parts]
  );
  const groupSelectionsList = useParts(groupSelectionsRefs);

  const sortRefs = useMemo(() => parts.map(part => part.sortRef), [parts]);
  const sorts = useParts(sortRefs);

  const filtersRefs = useMemo(
    () => parts.map(part => part.filtersRef),
    [parts]
  );
  const filtersList = useParts(filtersRefs);

  const singleRunName = useSingleRunName();

  // useRunSets is always used to construct a query to send to the backend.
  // Our various query layers (apollo useQuery, useBucketedQuery, useRunsData)
  // all have cache mechanisms that can immediately return a result if the
  // query is already in flight.
  //
  // We want all user interactions to cause a paint within 100ms of the action,
  // to avoid "sluggishness".
  //
  // Consider this scenario:
  // - User clicks an eyeball to start a query
  // - action is synchronously dispatched to redux
  // - reducer synchronously applies the action
  // - selectors synchronously run, including useRunSets (useParts above)
  // - useBucketedQuery depends on useRunSets, which runs synchronously
  // - if that hits cache, we synchronously render all line plots (which is very expensive)
  //
  // All of the above happens after the user's click, but before a paint occurs!
  //
  // So we use useDefferedMemo here to "break the path" by a tick. This means
  // that the line plots won't render synchronously with the user's click, removing 1s of
  // delay or more before the user sees something.

  return useDeferredMemo(
    () =>
      parts.map((part, i) => ({
        ...assembleRunSet(
          part,
          groupSelectionsList[i],
          sorts[i],
          filtersList[i],
          singleRunName
        ),
        ref: refs[i],
      })),
    [parts, groupSelectionsList, sorts, filtersList, singleRunName, refs]
  );
}

/**
 * If a project is explicitly saved on the run set config, we must use that.
 * Project details on the run set include entity name, project name, and
 * potentially project ID. Older run sets may contain only entity/project
 * names and no ID. Some run sets may not contain any project details at all,
 * in which case we'll fall back to the current project (parsed from URL).
 */
export const useRunSetProject = (
  runSetProject: RunSetPart['project']
): {
  entityName: string;
  projectName: string;
  projectInternalId?: string;
} => {
  // Entity/project names on run set config could become stale if project
  // is moved or renamed, so we should use project ID to look up latest names
  const internalIdFromRunSet = runSetProject?.id;
  const projectNamesFromIdQuery = useProjectNameFromInternalIdQuery({
    variables: {internalId: internalIdFromRunSet ?? ''},
    skip: internalIdFromRunSet == null,
  });
  const entityNameFromRunSet =
    projectNamesFromIdQuery.data?.project?.entityName ??
    runSetProject?.entityName;
  const projectNameFromRunSet =
    projectNamesFromIdQuery.data?.project?.name ?? runSetProject?.name;

  // If project ID is not available on the run set config,
  // use entity/project names (from run set or URL) to look it up
  const {entityName: entityNameFromUrl, projectName: projectNameFromUrl} =
    useProjectParams();
  const projectIdFromNamesQuery = useProjectInternalIdFromNameQuery({
    variables: {
      entityName: entityNameFromRunSet ?? entityNameFromUrl,
      projectName: projectNameFromRunSet ?? projectNameFromUrl,
    },
    skip: internalIdFromRunSet != null,
  });
  const internalIdFromNames = projectIdFromNamesQuery.data?.project?.internalId;

  return {
    entityName: entityNameFromRunSet ?? entityNameFromUrl,
    projectName: projectNameFromRunSet ?? projectNameFromUrl,
    projectInternalId: internalIdFromRunSet ?? internalIdFromNames,
  };
};
